// src/cockpit4/components/OKRManager.tsx
// ============================================
// OKR 目标管理体系（P1-功能5）- v2.0 详细改进计划
//
// 设计：完整的 OKR 管理体系
//   1. 树形结构展示 OKR 层级：年度目标(O) → 季度OKR(O) → 月度KR → 周计划 → 日任务（五层下钻）
//   2. 每个节点：标题、负责人、进度条、状态（进行中/已完成/风险）
//   3. 支持创建/编辑/删除 Objective 和 KeyResult（弹窗表单）
//   4. KR 进度可视化（进度条+百分比）
//   5. 风险 KR 自动标记（进度滞后 <30% 或截止日临近，红色高亮）
//   6. OKR 数据持久化到 Vault：11_Strategy/OKR/{年}/{季度}.md
//   7. 周期结束可一键生成复盘报告
//
// 数据来源：localStorage 持久化（key: xeaos_okr_2026），无 mock 数据，初始为空让用户自建
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Target,
  Plus,
  Loader2,
  RefreshCw,
  Pencil,
  Trash2,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  CheckCircle2,
  Clock,
  X,
  FileText,
  TrendingUp,
  Flag,
  Save,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 节点状态 */
type NodeStatus = "in_progress" | "completed" | "at_risk";

/** 计划层级：年/季/月/周/日 */
type PlanLevel = "yearly" | "quarterly" | "monthly" | "weekly" | "daily";

/** OKR 树节点（统一结构，支持五层下钻） */
interface OKRNode {
  /** 唯一 ID */
  id: string;
  /** 层级 */
  level: PlanLevel;
  /** 标题 */
  title: string;
  /** 描述 */
  description?: string;
  /** 负责人 */
  owner: string;
  /** 状态 */
  status: NodeStatus;
  /** 进度 0-100 */
  progress: number;
  /** 目标值（KR 用） */
  targetValue?: number;
  /** 当前值（KR 用） */
  currentValue?: number;
  /** 单位 */
  unit?: string;
  /** 截止日期 YYYY-MM-DD */
  dueDate?: string;
  /** 创建时间 */
  createdAt: string;
  /** 更新时间 */
  updatedAt: string;
  /** 子节点（五层下钻） */
  children: OKRNode[];
}

/** OKR 根数据结构：按年份分组 */
interface OKRYearData {
  year: number;
  /** 年度目标列表 */
  objectives: OKRNode[];
}

// ============================================
// 常量配置
// ============================================

const STORAGE_KEY = "xeaos_okr_2026";

const LEVEL_CONFIG: Record<PlanLevel, { label: string; color: string; icon: typeof Target }> = {
  yearly: { label: "年度目标", color: "#A855F7", icon: Flag },
  quarterly: { label: "季度 OKR", color: "#3B82F6", icon: Target },
  monthly: { label: "月度 KR", color: "#10B981", icon: TrendingUp },
  weekly: { label: "周计划", color: "#F59E0B", icon: Clock },
  daily: { label: "日任务", color: "#EC4899", icon: CheckCircle2 },
};

const STATUS_CONFIG: Record<NodeStatus, { label: string; color: string }> = {
  in_progress: { label: "进行中", color: "#3B82F6" },
  completed: { label: "已完成", color: "#10B981" },
  at_risk: { label: "风险", color: "#EF4444" },
};

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(): string {
  return `okr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** 获取当前季度 */
function getCurrentQuarter(): string {
  const month = new Date().getMonth() + 1;
  return `Q${Math.ceil(month / 3)}`;
}

/** 计算节点状态：基于进度+截止日自动判断风险 */
function computeStatus(node: OKRNode): NodeStatus {
  if (node.status === "completed" || node.progress >= 100) return "completed";
  const now = Date.now();
  if (node.dueDate) {
    const dueMs = new Date(node.dueDate).getTime();
    const daysLeft = Math.ceil((dueMs - now) / (24 * 60 * 60 * 1000));
    // 截止日临近（3天内）且进度 <70%，或已过期未完成
    if (daysLeft < 0) return "at_risk";
    if (daysLeft <= 3 && node.progress < 70) return "at_risk";
  }
  // 进度滞后 <30%（仅在已有截止日或子节点的情况下标记，避免新建空节点被误判）
  if (node.progress < 30 && node.children.length > 0) return "at_risk";
  return "in_progress";
}

/** 递归计算节点进度（若有子节点，按子节点平均进度计算） */
function computeProgress(node: OKRNode): number {
  if (node.children.length === 0) {
    // 叶子节点：若提供了 currentValue/targetValue，自动计算
    if (node.targetValue && node.targetValue > 0 && node.currentValue !== undefined) {
      return Math.min(100, Math.round((node.currentValue / node.targetValue) * 100));
    }
    return node.progress;
  }
  // 有子节点：取子节点平均进度
  const childProgress = node.children.reduce(
    (sum, c) => sum + computeProgress(c),
    0
  );
  return Math.round(childProgress / node.children.length);
}

/** 递归刷新节点的状态和进度（基于子节点） */
// v6.3.25 容错：旧版本数据节点可能缺 children / level 字段非法（localStorage 手改或跨版本），
// 直接抛错会被 loadFromStorage 静默吞掉 → 用户数据"消失"只剩空态；这里做白名单校验 + 补空数组
const VALID_LEVELS: PlanLevel[] = ["yearly", "quarterly", "monthly", "weekly", "daily"];
function refreshNode(node: OKRNode): OKRNode {
  const safeLevel: PlanLevel = VALID_LEVELS.includes(node.level) ? node.level : "yearly";
  const children = Array.isArray(node.children) ? node.children : [];
  const refreshedChildren = children.map(refreshNode);
  const progress = refreshedChildren.length > 0
    ? Math.round(refreshedChildren.reduce((s, c) => s + c.progress, 0) / refreshedChildren.length)
    : (node.targetValue && node.targetValue > 0 && node.currentValue !== undefined
        ? Math.min(100, Math.round((node.currentValue / node.targetValue) * 100))
        : typeof node.progress === "number" ? node.progress : 0);
  // 状态：若已被标记 completed 则保留，否则按进度+截止日重算
  const status: NodeStatus = node.status === "completed" || progress >= 100
    ? "completed"
    : computeStatus({ ...node, level: safeLevel, progress, children: refreshedChildren });
  return { ...node, level: safeLevel, progress, status, children: refreshedChildren, updatedAt: new Date().toISOString() };
}

/** 递归查找节点及其父节点 */
function findNodeAndParent(
  nodes: OKRNode[],
  id: string,
  parent: OKRNode | null = null
): { node: OKRNode; parent: OKRNode | null } | null {
  for (const n of nodes) {
    if (n.id === id) return { node: n, parent };
    const found = findNodeAndParent(n.children, id, n);
    if (found) return found;
  }
  return null;
}

/** 递归更新节点（不可变） */
function updateNodeInTree(nodes: OKRNode[], id: string, updater: (n: OKRNode) => OKRNode): OKRNode[] {
  return nodes.map((n) => {
    if (n.id === id) return updater(n);
    if (n.children.length > 0) {
      return { ...n, children: updateNodeInTree(n.children, id, updater) };
    }
    return n;
  });
}

/** 递归删除节点 */
function removeNodeFromTree(nodes: OKRNode[], id: string): OKRNode[] {
  return nodes
    .filter((n) => n.id !== id)
    .map((n) => ({
      ...n,
      children: removeNodeFromTree(n.children, id),
    }));
}

/** 添加子节点 */
function addChildToNode(nodes: OKRNode[], parentId: string, child: OKRNode): OKRNode[] {
  return nodes.map((n) => {
    if (n.id === parentId) {
      return { ...n, children: [...n.children, child] };
    }
    if (n.children.length > 0) {
      return { ...n, children: addChildToNode(n.children, parentId, child) };
    }
    return n;
  });
}

// ============================================
// 主组件
// ============================================

export default function OKRManager() {
  const [yearData, setYearData] = useState<OKRYearData>({
    year: new Date().getFullYear(),
    objectives: [],
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editingNode, setEditingNode] = useState<{ node: OKRNode | null; parentId: string | null; level: PlanLevel } | null>(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewContent, setReviewContent] = useState<string>("");

  // ============================================
  // 数据加载 / 持久化（localStorage）
  // ============================================
  const loadFromStorage = useCallback(() => {
    setLoading(true);
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as OKRYearData;
        // v6.3.25 容错：结构不兼容时（非 {year, objectives} 或 objectives 非数组）不静默清空用户数据
        const objectives = Array.isArray(parsed?.objectives) ? parsed.objectives : [];
        // 刷新所有节点的进度和状态
        const refreshed = { ...parsed, year: parsed?.year || new Date().getFullYear(), objectives: objectives.map(refreshNode) };
        setYearData(refreshed);
        // 默认展开所有年度目标
        setExpandedIds(new Set(refreshed.objectives.map((o) => o.id)));
      } else {
        // 初始为空，让用户自建
        setYearData({ year: new Date().getFullYear(), objectives: [] });
      }
    } catch {
      setYearData({ year: new Date().getFullYear(), objectives: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  const saveToStorage = useCallback((data: OKRYearData) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // 存储失败非阻塞
    }
  }, []);

  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  // 数据变更时自动保存
  const updateData = useCallback((updater: (prev: OKRYearData) => OKRYearData) => {
    setYearData((prev) => {
      const next = updater(prev);
      saveToStorage(next);
      return next;
    });
  }, [saveToStorage]);

  // ============================================
  // 节点操作
  // ============================================

  /** 创建/编辑节点（保存表单） */
  const handleSaveNode = useCallback((formNode: OKRNode, parentId: string | null) => {
    updateData((prev) => {
      // 编辑已有节点
      const existing = findNodeAndParent(prev.objectives, formNode.id);
      if (existing) {
        const updated = updateNodeInTree(prev.objectives, formNode.id, (n) => {
          const merged = { ...n, ...formNode, updatedAt: new Date().toISOString() };
          const progress = computeProgress(merged);
          const status = merged.status === "completed" || progress >= 100
            ? "completed"
            : computeStatus({ ...merged, progress });
          return { ...merged, progress, status };
        });
        return { ...prev, objectives: updated.map(refreshNode) };
      }
      // 新建节点
      const newNode: OKRNode = {
        ...formNode,
        id: genId(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        children: [],
      };
      if (parentId === null) {
        // 顶层年度目标
        return { ...prev, objectives: [...prev.objectives, newNode] };
      }
      const withChild = addChildToNode(prev.objectives, parentId, newNode);
      return { ...prev, objectives: withChild.map(refreshNode) };
    });
    setEditingNode(null);
    setMessage({ type: "ok", text: "✅ OKR 节点已保存（已持久化到 localStorage）" });
  }, [updateData]);

  /** 删除节点 */
  const handleDeleteNode = useCallback((nodeId: string) => {
    if (!confirm("确定删除该节点及其所有子节点？此操作不可撤销。")) return;
    updateData((prev) => ({
      ...prev,
      objectives: removeNodeFromTree(prev.objectives, nodeId),
    }));
    setMessage({ type: "ok", text: "✅ 节点已删除" });
  }, [updateData]);

  /** 切换展开/折叠 */
  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** 添加子节点 */
  const handleAddChild = useCallback((parentId: string, parentLevel: PlanLevel) => {
    const nextLevel: PlanLevel =
      parentLevel === "yearly" ? "quarterly"
      : parentLevel === "quarterly" ? "monthly"
      : parentLevel === "monthly" ? "weekly"
      : "daily";
    setEditingNode({ node: null, parentId, level: nextLevel });
  }, []);

  /** 编辑节点 */
  const handleEditNode = useCallback((node: OKRNode) => {
    setEditingNode({ node, parentId: null, level: node.level });
  }, []);

  // ============================================
  // 持久化到 Vault：11_Strategy/OKR/{年}/{季度}.md
  // ============================================
  const persistToVault = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const year = yearData.year;
      const quarter = getCurrentQuarter();
      // 过滤出当前季度的 OKR（季度 OKR 层级）
      const quarterlyOKRs = yearData.objectives.flatMap((o) =>
        o.children.filter((c) => c.level === "quarterly")
      );

      const content = [
        `---`,
        `type: okr_data`,
        `year: ${year}`,
        `quarter: ${quarter}`,
        `created_at: ${new Date().toISOString()}`,
        `objectives_count: ${yearData.objectives.length}`,
        `quarterly_okr_count: ${quarterlyOKRs.length}`,
        `---`,
        ``,
        `# 🎯 OKR 目标管理 · ${year}年 ${quarter}`,
        ``,
        `> 自动同步于 ${new Date().toLocaleString("zh-CN", { hour12: false })}`,
        ``,
        `## 年度目标概览`,
        ``,
        ...yearData.objectives.map((o, i) => {
          const childProg = o.children.length > 0
            ? Math.round(o.children.reduce((s, c) => s + c.progress, 0) / o.children.length)
            : o.progress;
          return `${i + 1}. **${o.title}**（负责人：${o.owner}，进度：${childProg}%，状态：${STATUS_CONFIG[o.status].label}）`;
        }),
        ``,
        `## 季度 OKR 明细`,
        ``,
        ...quarterlyOKRs.map((q, i) => {
          const lines: string[] = [];
          lines.push(`### ${i + 1}. ${q.title}`);
          lines.push(`- 负责人：${q.owner}`);
          lines.push(`- 进度：${q.progress}%（状态：${STATUS_CONFIG[q.status].label}）`);
          if (q.targetValue !== undefined && q.currentValue !== undefined) {
            lines.push(`- 当前值：${q.currentValue} / ${q.targetValue} ${q.unit || ""}`);
          }
          if (q.dueDate) lines.push(`- 截止日：${q.dueDate}`);
          if (q.children.length > 0) {
            lines.push(`- 月度 KR：`);
            q.children.forEach((m) => {
              lines.push(`  - ${m.title}（${m.progress}%，${STATUS_CONFIG[m.status].label}）`);
            });
          }
          lines.push("");
          return lines.join("\n");
        }),
        ``,
        `## 风险 KR 预警`,
        ``,
        ...(function collectRisks(nodes: OKRNode[], acc: string[]): string[] {
          for (const n of nodes) {
            if (n.status === "at_risk") {
              acc.push(`- ⚠️ **${n.title}**（${LEVEL_CONFIG[n.level].label}）— 进度 ${n.progress}%${n.dueDate ? `，截止 ${n.dueDate}` : ""}`);
            }
            if (n.children.length > 0) collectRisks(n.children, acc);
          }
          return acc;
        })(yearData.objectives, []),
        ``,
        `---`,
        `生成时间：${new Date().toISOString()}`,
      ].join("\n");

      await fetch(
        `/vault-api/write?path=${encodeURIComponent(`11_Strategy/OKR/${year}/${quarter}.md`)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content }),
        }
      );

      setMessage({
        type: "ok",
        text: `✅ OKR 数据已写入 Vault：11_Strategy/OKR/${year}/${quarter}.md`,
      });
    } catch (err) {
      setMessage({
        type: "err",
        text: `❌ Vault 写入失败：${(err as Error).message}`,
      });
    } finally {
      setLoading(false);
    }
  }, [yearData]);

  // ============================================
  // 生成复盘报告
  // ============================================
  const generateReview = useCallback(async () => {
    setLoading(true);
    try {
      const year = yearData.year;
      const quarter = getCurrentQuarter();
      const allNodes: OKRNode[] = [];
      const collect = (nodes: OKRNode[]) => {
        for (const n of nodes) {
          allNodes.push(n);
          if (n.children.length > 0) collect(n.children);
        }
      };
      collect(yearData.objectives);

      const completed = allNodes.filter((n) => n.status === "completed");
      const atRisk = allNodes.filter((n) => n.status === "at_risk");
      const inProgress = allNodes.filter((n) => n.status === "in_progress");
      const avgProgress = allNodes.length > 0
        ? Math.round(allNodes.reduce((s, n) => s + n.progress, 0) / allNodes.length)
        : 0;

      const content = [
        `---`,
        `type: okr_review`,
        `year: ${year}`,
        `quarter: ${quarter}`,
        `created_at: ${new Date().toISOString()}`,
        `total_nodes: ${allNodes.length}`,
        `completed: ${completed.length}`,
        `at_risk: ${atRisk.length}`,
        `in_progress: ${inProgress.length}`,
        `avg_progress: ${avgProgress}`,
        `---`,
        ``,
        `# 📊 OKR 复盘报告 · ${year}年 ${quarter}`,
        ``,
        `> 生成时间：${new Date().toLocaleString("zh-CN", { hour12: false })}`,
        ``,
        `## 整体完成情况`,
        ``,
        `- 总节点数：${allNodes.length}`,
        `- 已完成：${completed.length}（${allNodes.length > 0 ? Math.round(completed.length / allNodes.length * 100) : 0}%）`,
        `- 进行中：${inProgress.length}`,
        `- 风险项：${atRisk.length}`,
        `- 平均进度：${avgProgress}%`,
        ``,
        `## 完成亮点`,
        ``,
        ...completed.slice(0, 10).map((n) => `- ✅ **${n.title}**（${LEVEL_CONFIG[n.level].label}，负责人：${n.owner}）`),
        ...(completed.length === 0 ? ["- 暂无已完成的节点"] : []),
        ``,
        `## 风险与改进`,
        ``,
        ...atRisk.map((n) => `- ⚠️ **${n.title}**（${LEVEL_CONFIG[n.level].label}）— 进度 ${n.progress}%${n.dueDate ? `，截止 ${n.dueDate}` : ""}`),
        ...(atRisk.length === 0 ? ["- 暂无风险项"] : []),
        ``,
        `## 复盘总结`,
        ``,
        `本季度共制定 ${allNodes.length} 个 OKR 节点，完成率 ${allNodes.length > 0 ? Math.round(completed.length / allNodes.length * 100) : 0}%，平均进度 ${avgProgress}%。`,
        atRisk.length > 0
          ? `仍有 ${atRisk.length} 个节点存在风险，需在下个周期重点关注。`
          : `所有节点按计划推进，继续保持。`,
        ``,
        `---`,
        `复盘人：CEO Workspace`,
      ].join("\n");

      setReviewContent(content);
      setShowReviewDialog(true);

      // 同时写入 Vault
      await fetch(
        `/vault-api/write?path=${encodeURIComponent(`11_Strategy/OKR/${year}/${quarter}-复盘.md`)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content }),
        }
      );
      setMessage({
        type: "ok",
        text: `✅ 复盘报告已生成并写入 Vault：11_Strategy/OKR/${year}/${quarter}-复盘.md`,
      });
    } catch (err) {
      setMessage({
        type: "err",
        text: `❌ 复盘报告生成失败：${(err as Error).message}`,
      });
    } finally {
      setLoading(false);
    }
  }, [yearData]);

  // ============================================
  // 统计
  // ============================================
  const stats = useMemo(() => {
    const allNodes: OKRNode[] = [];
    const collect = (nodes: OKRNode[]) => {
      for (const n of nodes) {
        allNodes.push(n);
        if (n.children.length > 0) collect(n.children);
      }
    };
    collect(yearData.objectives);
    return {
      total: allNodes.length,
      objectives: yearData.objectives.length,
      completed: allNodes.filter((n) => n.status === "completed").length,
      atRisk: allNodes.filter((n) => n.status === "at_risk").length,
      inProgress: allNodes.filter((n) => n.status === "in_progress").length,
      avgProgress: allNodes.length > 0
        ? Math.round(allNodes.reduce((s, n) => s + n.progress, 0) / allNodes.length)
        : 0,
    };
  }, [yearData]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
            <Target size={13} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">OKR 目标管理体系</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              OKR Manager · {yearData.year} · 总 {stats.total} · 完成 {stats.completed} · 风险 {stats.atRisk} · 平均 {stats.avgProgress}%
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 复盘报告 */}
          <button
            onClick={generateReview}
            disabled={loading || stats.total === 0}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
            title="生成 OKR 复盘报告"
          >
            <FileText size={10} className="text-amber-400" />
            <span className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">复盘报告</span>
          </button>
          {/* 写入 Vault */}
          <button
            onClick={persistToVault}
            disabled={loading || stats.total === 0}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors disabled:opacity-50"
            title="同步 OKR 数据到 Vault"
          >
            <Save size={10} className="text-cyan-400" />
            <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">同步 Vault</span>
          </button>
          {/* 新建年度目标 */}
          <button
            onClick={() => setEditingNode({ node: null, parentId: null, level: "yearly" })}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors"
            title="新建年度目标"
          >
            {loading ? (
              <Loader2 size={11} className="text-purple-400 animate-spin" />
            ) : (
              <Plus size={11} className="text-purple-400" />
            )}
            <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">新建目标</span>
          </button>
          <button
            onClick={loadFromStorage}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            <RefreshCw size={11} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div
            className={`px-3 py-2 rounded-md border text-[10px] font-mono whitespace-pre-line ${
              message.type === "ok"
                ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                : "border-red-500/30 bg-red-500/[0.06] text-red-300"
            }`}
          >
            {message.text}
          </div>
        </div>
      )}

      {/* 内容区：树形结构 */}
      <div className="p-4 max-h-[560px] overflow-y-auto">
        {yearData.objectives.length === 0 ? (
          <div className="py-12 text-center">
            <Target size={28} className="mx-auto text-gray-700 mb-3" />
            <p className="text-[12px] font-mono text-gray-500 mb-2">
              暂无 OKR 数据
            </p>
            <p className="text-[10px] font-mono text-gray-600">
              点击右上角「新建目标」创建第一个年度目标
            </p>
            <p className="text-[9px] font-mono text-gray-700 mt-3">
              支持五层下钻：年度目标 → 季度 OKR → 月度 KR → 周计划 → 日任务
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {yearData.objectives.map((obj, idx) => (
              <OKRTreeNode
                key={obj.id}
                node={obj}
                depth={0}
                index={idx}
                expandedIds={expandedIds}
                onToggle={toggleExpand}
                onAddChild={handleAddChild}
                onEdit={handleEditNode}
                onDelete={handleDeleteNode}
              />
            ))}
          </div>
        )}
      </div>

      {/* 编辑表单 Modal */}
      <AnimatePresence>
        {editingNode && (
          <OKREditDialog
            initial={editingNode.node}
            level={editingNode.level}
            parentId={editingNode.parentId}
            onClose={() => setEditingNode(null)}
            onSave={handleSaveNode}
          />
        )}
      </AnimatePresence>

      {/* 复盘报告预览 Modal */}
      <AnimatePresence>
        {showReviewDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowReviewDialog(false)}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl rounded-xl border border-white/[0.08] bg-[#0a0e1a] overflow-hidden flex flex-col max-h-[80vh]"
            >
              <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
              <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-amber-400" />
                  <h3 className="text-[13px] font-semibold text-white">OKR 复盘报告</h3>
                </div>
                <button
                  onClick={() => setShowReviewDialog(false)}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05]"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <pre className="text-[11px] font-mono text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {reviewContent}
                </pre>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：OKR 树节点（递归渲染）
// ============================================

interface OKRTreeNodeProps {
  node: OKRNode;
  depth: number;
  index: number;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  onAddChild: (parentId: string, parentLevel: PlanLevel) => void;
  onEdit: (node: OKRNode) => void;
  onDelete: (id: string) => void;
}

function OKRTreeNode({
  node,
  depth,
  index,
  expandedIds,
  onToggle,
  onAddChild,
  onEdit,
  onDelete,
}: OKRTreeNodeProps) {
  const levelCfg = LEVEL_CONFIG[node.level];
  const statusCfg = STATUS_CONFIG[node.status];
  const Icon = levelCfg.icon;
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isRisk = node.status === "at_risk";
  const isCompleted = node.status === "completed";
  const canAddChild = node.level !== "daily"; // 日任务不能再加子节点

  return (
    <div className="relative">
      {/* 节点本体 */}
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.2, delay: depth * 0.03 }}
        className={`relative rounded-md border p-2.5 transition-all ${
          isRisk
            ? "border-red-500/30 bg-red-500/[0.04]"
            : isCompleted
            ? "border-emerald-500/20 bg-emerald-500/[0.03]"
            : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
        }`}
        style={{ marginLeft: `${depth * 20}px` }}
      >
        {/* 左侧层级色条 */}
        <div
          className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full"
          style={{ background: levelCfg.color }}
        />

        <div className="flex items-center gap-2 pl-2">
          {/* 展开/折叠按钮 */}
          {hasChildren ? (
            <button
              onClick={() => onToggle(node.id)}
              className="flex-shrink-0 w-4 h-4 rounded flex items-center justify-center hover:bg-white/[0.08] transition-colors"
            >
              {isExpanded ? (
                <ChevronDown size={11} className="text-gray-400" />
              ) : (
                <ChevronRight size={11} className="text-gray-400" />
              )}
            </button>
          ) : (
            <span className="w-4 h-4 flex-shrink-0" />
          )}

          {/* 图标 */}
          <div
            className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center"
            style={{ background: `${levelCfg.color}15` }}
          >
            <Icon size={10} style={{ color: levelCfg.color }} />
          </div>

          {/* 层级标签 */}
          <span
            className="flex-shrink-0 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase"
            style={{ background: `${levelCfg.color}15`, color: levelCfg.color }}
          >
            {levelCfg.label}
          </span>

          {/* 标题 */}
          <span className={`text-[12px] truncate flex-1 ${isCompleted ? "text-gray-400 line-through" : "text-gray-200"}`}>
            {node.title}
          </span>

          {/* 负责人 */}
          {node.owner && (
            <span className="text-[9px] font-mono text-gray-500 flex-shrink-0">
              @ {node.owner}
            </span>
          )}

          {/* 进度条 */}
          <div className="flex items-center gap-1.5 flex-shrink-0 w-32">
            <div className="flex-1 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${node.progress}%`,
                  background: isRisk ? statusCfg.color : isCompleted ? statusCfg.color : levelCfg.color,
                }}
              />
            </div>
            <span
              className="text-[9px] font-mono font-bold w-8 text-right"
              style={{ color: isRisk ? statusCfg.color : isCompleted ? statusCfg.color : levelCfg.color }}
            >
              {node.progress}%
            </span>
          </div>

          {/* 状态标签 */}
          <span
            className="flex-shrink-0 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase flex items-center gap-0.5"
            style={{ background: `${statusCfg.color}15`, color: statusCfg.color }}
          >
            {isRisk && <AlertTriangle size={8} />}
            {isCompleted && <CheckCircle2 size={8} />}
            {statusCfg.label}
          </span>

          {/* 截止日 */}
          {node.dueDate && (
            <span className="flex-shrink-0 text-[9px] font-mono text-gray-600 flex items-center gap-0.5">
              <Clock size={8} />
              {node.dueDate}
            </span>
          )}

          {/* 操作按钮 */}
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {canAddChild && (
              <button
                onClick={() => onAddChild(node.id, node.level)}
                className="p-1 rounded hover:bg-white/[0.08] transition-colors"
                title={`添加${LEVEL_CONFIG[node.level === "yearly" ? "quarterly" : node.level === "quarterly" ? "monthly" : node.level === "monthly" ? "weekly" : "daily"].label}`}
              >
                <Plus size={10} className="text-gray-500 hover:text-gray-300" />
              </button>
            )}
            <button
              onClick={() => onEdit(node)}
              className="p-1 rounded hover:bg-white/[0.08] transition-colors"
              title="编辑"
            >
              <Pencil size={10} className="text-gray-500 hover:text-cyan-300" />
            </button>
            <button
              onClick={() => onDelete(node.id)}
              className="p-1 rounded hover:bg-white/[0.08] transition-colors"
              title="删除"
            >
              <Trash2 size={10} className="text-gray-500 hover:text-red-400" />
            </button>
          </div>
        </div>

        {/* 数值显示（KR 类型） */}
        {node.targetValue !== undefined && node.currentValue !== undefined && (
          <div className="mt-1.5 pl-10 text-[10px] font-mono text-gray-500">
            当前 <span className="text-gray-300">{node.currentValue}</span> / 目标 <span className="text-gray-300">{node.targetValue}</span> {node.unit || ""}
          </div>
        )}
      </motion.div>

      {/* 子节点（连接线 + 递归） */}
      <AnimatePresence initial={false}>
        {hasChildren && isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="relative overflow-hidden"
          >
            {/* 连接线 */}
            <div
              className="absolute top-0 bottom-4 w-px bg-white/[0.06]"
              style={{ left: `${depth * 20 + 18}px` }}
            />
            <div className="space-y-1.5 pt-1.5">
              {node.children.map((child, idx) => (
                <OKRTreeNode
                  key={child.id}
                  node={child}
                  depth={depth + 1}
                  index={idx}
                  expandedIds={expandedIds}
                  onToggle={onToggle}
                  onAddChild={onAddChild}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 子组件：OKR 编辑表单 Modal
// ============================================

interface OKREditDialogProps {
  initial: OKRNode | null;
  level: PlanLevel;
  parentId: string | null;
  onClose: () => void;
  onSave: (node: OKRNode, parentId: string | null) => void;
}

function OKREditDialog({ initial, level, parentId, onClose, onSave }: OKREditDialogProps) {
  const levelCfg = LEVEL_CONFIG[level];
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [owner, setOwner] = useState(initial?.owner || "");
  const [targetValue, setTargetValue] = useState<string>(initial?.targetValue?.toString() || "");
  const [currentValue, setCurrentValue] = useState<string>(initial?.currentValue?.toString() || "");
  const [unit, setUnit] = useState(initial?.unit || "");
  const [dueDate, setDueDate] = useState(initial?.dueDate || "");
  const [status, setStatus] = useState<NodeStatus>(initial?.status || "in_progress");
  const [progress, setProgress] = useState(initial?.progress.toString() || "0");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    const node: OKRNode = {
      id: initial?.id || genId(),
      level,
      title: title.trim(),
      description: description.trim() || undefined,
      owner: owner.trim() || "未指派",
      status,
      progress: Math.min(100, Math.max(0, parseInt(progress) || 0)),
      targetValue: targetValue ? Number(targetValue) : undefined,
      currentValue: currentValue ? Number(currentValue) : undefined,
      unit: unit.trim() || undefined,
      dueDate: dueDate || undefined,
      createdAt: initial?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      children: initial?.children || [],
    };
    onSave(node, parentId);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 10 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-xl border border-white/[0.08] bg-[#0a0e1a] overflow-hidden"
      >
        <div className="h-1" style={{ background: `linear-gradient(90deg, ${levelCfg.color}, ${levelCfg.color}80)` }} />
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <levelCfg.icon size={14} style={{ color: levelCfg.color }} />
            <h3 className="text-[13px] font-semibold text-white">
              {initial ? "编辑" : "新建"} {levelCfg.label}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05]"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {/* 标题 */}
          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              标题 *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              required
              placeholder={`请输入${levelCfg.label}标题`}
              className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/40"
            />
          </div>

          {/* 描述 */}
          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              描述
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="详细描述（可选）"
              className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/40 resize-none"
            />
          </div>

          {/* 负责人 + 截止日 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                负责人
              </label>
              <input
                type="text"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                placeholder="AI 岗位或人名"
                className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/40"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                截止日
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 focus:outline-none focus:border-cyan-500/40"
              />
            </div>
          </div>

          {/* KR 数值（仅月度/季度层级显示） */}
          {(level === "monthly" || level === "quarterly") && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                  当前值
                </label>
                <input
                  type="number"
                  value={currentValue}
                  onChange={(e) => setCurrentValue(e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/40"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                  目标值
                </label>
                <input
                  type="number"
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                  placeholder="100"
                  className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/40"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                  单位
                </label>
                <input
                  type="text"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  placeholder="个/元/%"
                  className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/40"
                />
              </div>
            </div>
          )}

          {/* 进度 + 状态 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                进度：{progress}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={progress}
                onChange={(e) => setProgress(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                状态
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as NodeStatus)}
                className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 focus:outline-none focus:border-cyan-500/40"
              >
                <option value="in_progress">进行中</option>
                <option value="completed">已完成</option>
                <option value="at_risk">风险</option>
              </select>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.06]">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-[11px] font-mono text-gray-400 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] rounded transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono text-white rounded transition-colors"
              style={{ background: `${levelCfg.color}20`, border: `1px solid ${levelCfg.color}40` }}
            >
              <Save size={11} />
              保存
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
